import { StockDispositionPreview } from "../components/StockDispositionPreview";
import { useState, useEffect, type FormEvent } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { Skeleton } from "@/components/base/Skeleton";
import { Modal } from "@/components/base/Modal";
import { money } from "@/lib/format";
import { cleanupCatalogueMediaAssets, signMediaUrls, SIGNED_MEDIA_REFRESH_MS } from "@/lib/media";
import { canManageProducts } from "@/lib/permissions";
import * as productApi from "@/lib/api/products";
import { retireCatalogueProduct } from "@/lib/api/listing-sync";
import { effectiveUnitOfMeasure, isSimpleProduct, variantSectionLabel, sellingPriceSummary, costPriceSummary } from "@/lib/products";
import { CANONICAL_CONDITION_OPTIONS, conditionSelectValue, sanitizeManufacturerIdentifier } from "@/lib/catalogue-facts";
import { firstRelation } from "@/lib/relations";
import { isCanonicalProductId } from "@/lib/phase1-sync-contract";
import { resolveLedgerReferences, type ReferenceInfo } from "@/lib/references";
import { flattenWithDepth, findDuplicateCategory, type Category } from "@/lib/categories";
import MediaTab from "@/pages/products/detail/components/MediaTab";
import ChannelsTab from "@/pages/products/detail/components/ChannelsTab";
import BundleTab from "@/pages/products/detail/components/BundleTab";
import HistoryTab from "@/pages/products/detail/components/HistoryTab";
import OverviewTab from "@/pages/products/detail/components/OverviewTab";
import VariantsTab from "@/pages/products/detail/components/VariantsTab";
import InventoryTab from "@/pages/products/detail/components/InventoryTab";
import SuppliersTab from "@/pages/products/detail/components/SuppliersTab";
import AdvancedTab from "@/pages/products/detail/components/AdvancedTab";
import StorefrontTab from "@/pages/products/detail/components/StorefrontTab";
import PostCreateAttentionBanner from "@/pages/products/detail/components/PostCreateAttentionBanner";
import { QuickVariantPriceModal } from "@/pages/products/components/QuickVariantPriceModal";
import {
  enrichProduct,
  enrichableFieldsFromDraft,
  enrichmentDraftCaption,
  type EnrichmentDraft,
} from "@/lib/enrichment";
import { enrichmentNeedsOverwriteConfirm, mergeEnrichmentFields } from "@/lib/ai-field-merge";
import { optionFactsFromVariants } from "@/lib/product-ai-boundary";
import { optionGroupsFromVariants } from "@/lib/product-seo";
import {
  inputCls,
  labelCls,
  type ActiveTab,
  type ArchiveReadiness,
  type BalanceRow,
  type DeleteSafety,
  type ExternalIdRow,
  type LedgerRow,
  type ProductRow,
  type SupplierLinkRow,
  type VariantRow,
} from "@/pages/products/detail/types";

function ProductDetailLoading() {
  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <Skeleton className="w-8 h-8 rounded-lg" />
        <div className="space-y-1.5">
          <Skeleton className="w-56 h-6" />
          <Skeleton className="w-32 h-4" />
        </div>
      </div>
      <div className="mt-2 mb-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <Skeleton className="w-32 h-5 mb-5" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="w-20 h-4" />
              <Skeleton className="w-24 h-7" />
            </div>
          ))}
        </div>
        <Skeleton className="w-40 h-4 mb-2" />
        <Skeleton className="w-full h-4" />
        <Skeleton className="w-3/4 h-4 mt-2" />
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { org, role } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", reference: "", barcode: "", category_id: "", description: "", short_description: "", seo_title: "", meta_description: "", seo_keywords: "", tags: "", brand: "", cost_price: "", selling_price: "", reorder_point: "", reorder_qty: "", status: "active", weight_kg: "", length_cm: "", width_cm: "", height_cm: "", manufacturer: "", model_number: "", mpn: "", country_of_origin: "", hs_tariff_code: "", customs_description: "", unit_of_measure: "", tax_profile: "", shipping_profile: "", condition: "" });
  const [catQuickAdd, setCatQuickAdd] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [catQuickError, setCatQuickError] = useState("");
  const [catQuickSaving, setCatQuickSaving] = useState(false);
  const [aiDescOpen, setAiDescOpen] = useState(false);
  const [aiDescLoading, setAiDescLoading] = useState(false);
  const [enrichDraft, setEnrichDraft] = useState<EnrichmentDraft | null>(null);
  const [rewriteExisting, setRewriteExisting] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishSaving, setPublishSaving] = useState(false);
  const [publishError, setPublishError] = useState("");
  const [publishForm, setPublishForm] = useState({ connector_id: "", channel_qty: "", safety_stock: "" });
  const [activeTab, setActiveTab] = useState<ActiveTab>("overview");
  const [priceMatrixOpen, setPriceMatrixOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupName, setDupName] = useState("");
  const [dupBusy, setDupBusy] = useState(false);
  const [dupError, setDupError] = useState("");
  const [lifecycleError, setLifecycleError] = useState("");
  const [lifecycleNotice, setLifecycleNotice] = useState("");
  const [sellingStateOpen, setSellingStateOpen] = useState(false);
  const [sellingStateAction, setSellingStateAction] = useState<"pause" | "resume">("pause");
  const [sellingStateBusy, setSellingStateBusy] = useState(false);
  const [sellingStateTracking, setSellingStateTracking] = useState<{
    cycleId: string;
    action: "pause" | "resume";
  } | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveReadiness, setArchiveReadiness] = useState<ArchiveReadiness | null>(null);
  const [archiveError, setArchiveError] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSafety, setDeleteSafety] = useState<DeleteSafety | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteWriteOffStock, setDeleteWriteOffStock] = useState(false);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "channels") setActiveTab("channels");
  }, [searchParams]);

  useEffect(() => {
    if (!sellingStateTracking || !org?.id || !id) return;

    let cancelled = false;
    const desiredState = sellingStateTracking.action === "pause" ? "paused" : "resumed";
    const failedState = sellingStateTracking.action === "pause" ? "pause_failed" : "resume_failed";

    const refreshSellingState = async () => {
      const { data: rows, error: trackingError } = await supabase
        .from("product_sales_pause_channels")
        .select("state, last_error, connectors(name)")
        .eq("cycle_id", sellingStateTracking.cycleId);

      if (cancelled || trackingError || !rows?.length) return;

      const failed = rows.filter((row) => row.state === failedState);
      const pending = rows.filter((row) => row.state !== desiredState && row.state !== failedState);
      if (pending.length > 0) return;

      setSellingStateTracking(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["product-detail", org.id, id] }),
        queryClient.invalidateQueries({ queryKey: ["products", org.id] }),
        queryClient.invalidateQueries({ queryKey: ["catalogue-coverage", org.id] }),
        queryClient.invalidateQueries({ queryKey: ["channel-listings", org.id, id] }),
      ]);
      if (cancelled) return;

      if (failed.length > 0) {
        const names = failed.map((row) => {
          const connector = Array.isArray(row.connectors) ? row.connectors[0] : row.connectors;
          return connector?.name ?? "Sales channel";
        });
        setLifecycleNotice("");
        setLifecycleError(
          `${sellingStateTracking.action === "pause" ? "Pause" : "Resume"} completed with channel failures: ${names.join(", ")}. Review the Selling tab.`,
        );
      } else {
        setLifecycleError("");
        setLifecycleNotice(
          sellingStateTracking.action === "pause"
            ? `Selling paused on ${rows.length} channel${rows.length === 1 ? "" : "s"}.`
            : `Selling resumed on ${rows.length} channel${rows.length === 1 ? "" : "s"}.`,
        );
      }
    };

    void refreshSellingState();
    const timer = window.setInterval(() => void refreshSellingState(), 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [sellingStateTracking, org?.id, id, queryClient]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["product-detail", org?.id, id],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      if (!isCanonicalProductId(id)) {
        return {
          product: null as ProductRow | null,
          balances: [] as BalanceRow[],
          variants: [] as VariantRow[],
          ledger: [] as LedgerRow[],
          references: new Map<string, ReferenceInfo>(),
          categoriesList: [] as Category[],
          optionCount: 0,
          listingCount: 0,
        };
      }
      const [product, balances, variants, ledger, categoriesList, optionCountRes, listingCountRes] = await Promise.all([
        supabase.from("products").select("id, name, reference, sku, barcode, category_id, description, short_description, seo_title, meta_description, seo_keywords, url_slug, og_title, og_description, og_image_url, storefront_visible, is_featured, min_purchase_qty, max_purchase_qty, tags, brand, product_type, status, unit, cost_price, selling_price, reorder_point, reorder_qty, weight_kg, length_cm, width_cm, height_cm, manufacturer, model_number, mpn, country_of_origin, hs_tariff_code, customs_description, unit_of_measure, tax_profile, shipping_profile, condition, identifiers_not_applicable, categories(name)").eq("org_id", org!.id).eq("id", id).maybeSingle(),
        supabase.from("inventory_balances").select("id, variant_id, location_id, on_hand, allocated, available, incoming, transferring, locations(name), variants(sku, option_values)").eq("org_id", org!.id).eq("product_id", id),
        supabase.from("variants").select("id, sku, barcode, option_values, cost_price, selling_price, reorder_point, reorder_qty, weight_kg, length_cm, width_cm, height_cm, status").eq("org_id", org!.id).eq("product_id", id).order("position"),
        supabase.from("inventory_ledger").select("id, entry_type, qty_change, created_at, reason, reference_type, reference_id, locations(name)").eq("org_id", org!.id).eq("product_id", id).order("created_at", { ascending: false }).limit(20),
        supabase.from("categories").select("id, name, parent_id, status").eq("org_id", org!.id).order("name"),
        supabase.from("product_options").select("id", { count: "exact", head: true }).eq("org_id", org!.id).eq("product_id", id).eq("status", "active"),
        supabase.from("channel_listings").select("id", { count: "exact", head: true }).eq("org_id", org!.id).eq("product_id", id),
      ]);
      if (product.error) throw new Error(product.error.message);
      // Never turn an unavailable inventory read into a loaded empty balance map.
      if (balances.error) throw new Error(balances.error.message);
      const ledgerRows = (ledger.data ?? []).map((row) => ({
        ...row,
        locations: firstRelation(row.locations),
      })) as LedgerRow[];
      const references = await resolveLedgerReferences(org!.id, ledgerRows);
      return {
        product: product.data
          ? {
              ...product.data,
              categories: firstRelation(product.data.categories),
            } as ProductRow
          : null,
        balances: (balances.data ?? []).map((row) => ({
          ...row,
          locations: firstRelation(row.locations),
          variants: firstRelation(row.variants),
        })) as BalanceRow[],
        variants: (variants.data ?? []) as VariantRow[],
        ledger: ledgerRows,
        references,
        categoriesList: (categoriesList.data ?? []) as Category[],
        optionCount: optionCountRes.count ?? 0,
        listingCount: listingCountRes.count ?? 0,
      };
    },
  });

  const { data: channelConnectors } = useQuery({
    queryKey: ["connectors-for-publish", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const { data: rows } = await supabase
        .from("connectors")
        .select("id, name, provider, marketplace_site")
        .eq("org_id", org!.id)
        .eq("enabled", true)
        .order("name");
      return (rows ?? []) as { id: string; name: string; provider: string | null; marketplace_site: string | null }[];
    },
  });

  const { data: extData } = useQuery({
    queryKey: ["external-ids", org?.id, id],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      const [ids, connectors] = await Promise.all([
        supabase
          .from("external_identifiers")
          .select("id, connector_id, external_type, external_id, sync_status, created_at")
          .eq("org_id", org!.id)
          .eq("entity_type", "product")
          .eq("entity_id", id)
          .order("created_at", { ascending: false }),
        supabase.from("connectors").select("id, name").eq("org_id", org!.id).order("name"),
      ]);
      return {
        ids: (ids.data ?? []) as ExternalIdRow[],
        connectors: (connectors.data ?? []) as { id: string; name: string }[],
      };
    },
  });

  const { data: supData } = useQuery({
    queryKey: ["product-suppliers", org?.id, id],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      const [links, suppliers] = await Promise.all([
        supabase
          .from("supplier_products")
          .select("id, supplier_id, variant_id, supplier_sku, supplier_price, min_order_qty, order_multiple, lead_time_days, is_preferred, suppliers(name), variants(sku)")
          .eq("org_id", org!.id)
          .eq("product_id", id)
          .order("created_at"),
        supabase.from("suppliers").select("id, name").eq("org_id", org!.id).order("name"),
      ]);
      return {
        links: (links.data ?? []).map((row) => ({
          ...row,
          suppliers: firstRelation(row.suppliers),
          variants: firstRelation(row.variants),
        })) as SupplierLinkRow[],
        suppliers: (suppliers.data ?? []) as { id: string; name: string }[],
      };
    },
  });

  const { data: activityData } = useQuery({
    queryKey: ["product-activity", org?.id, id],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      const [poItems, soItems] = await Promise.all([
        supabase
          .from("purchase_order_items")
          .select("id, purchase_order_id, qty_ordered, qty_received")
          .eq("org_id", org!.id)
          .eq("product_id", id)
          .order("created_at", { ascending: false })
          .limit(6),
        supabase
          .from("sales_order_items")
          .select("id, sales_order_id, qty_ordered, qty_fulfilled")
          .eq("org_id", org!.id)
          .eq("product_id", id)
          .order("created_at", { ascending: false })
          .limit(6),
      ]);
      const poIds = Array.from(new Set((poItems.data ?? []).map((i) => i.purchase_order_id)));
      const soIds = Array.from(new Set((soItems.data ?? []).map((i) => i.sales_order_id)));
      const [pos, sos] = await Promise.all([
        poIds.length
          ? supabase.from("purchase_orders").select("id, po_number, status, order_date, supplier_id").in("id", poIds)
          : Promise.resolve({ data: [] }),
        soIds.length
          ? supabase.from("sales_orders").select("id, order_number, status, order_date, customer_id").in("id", soIds)
          : Promise.resolve({ data: [] }),
      ]);
      const supplierIds = Array.from(new Set((pos.data ?? []).map((p) => p.supplier_id).filter(Boolean)));
      const customerIds = Array.from(new Set((sos.data ?? []).map((s) => s.customer_id).filter(Boolean)));
      const [suppliers, customers] = await Promise.all([
        supplierIds.length ? supabase.from("suppliers").select("id, name").in("id", supplierIds) : Promise.resolve({ data: [] }),
        customerIds.length ? supabase.from("customers").select("id, name").in("id", customerIds) : Promise.resolve({ data: [] }),
      ]);
      const supplierName: Record<string, string> = {};
      (suppliers.data ?? []).forEach((s) => (supplierName[s.id] = s.name));
      const customerName: Record<string, string> = {};
      (customers.data ?? []).forEach((c) => (customerName[c.id] = c.name));
      const poMap: Record<string, { id: string; po_number: string; status: string; order_date: string | null; supplier_id: string | null }> = {};
      (pos.data ?? []).forEach((p) => (poMap[p.id] = p));
      const soMap: Record<string, { id: string; order_number: string; status: string; order_date: string | null; customer_id: string | null }> = {};
      (sos.data ?? []).forEach((s) => (soMap[s.id] = s));
      return {
        purchases: (poItems.data ?? [])
          .map((it) => ({ ...it, po: poMap[it.purchase_order_id] }))
          .filter((it) => it.po),
        sales: (soItems.data ?? [])
          .map((it) => ({ ...it, so: soMap[it.sales_order_id] }))
          .filter((it) => it.so),
        supplierName,
        customerName,
      };
    },
  });

  const canManage = canManageProducts(role);

  const { data: quality } = useQuery({
    queryKey: ["product-quality", org?.id, id],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      return productApi.evaluateQuality(org!.id, id!);
    },
  });

  const { data: historyData } = useQuery({
    queryKey: ["product-history", org?.id, id],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      const { data: hd } = await supabase
        .from("product_history")
        .select("id, field, old_value, new_value, variant_id, created_at")
        .eq("org_id", org!.id)
        .eq("product_id", id)
        .order("created_at", { ascending: false })
        .limit(50);
      return (hd ?? []) as { id: string; field: string; old_value: string | null; new_value: string | null; variant_id: string | null; created_at: string }[];
    },
  });

  const { data: primaryImage } = useQuery({
    queryKey: ["product-primary-image", org?.id, id],
    enabled: !!org?.id && !!id,
    staleTime: SIGNED_MEDIA_REFRESH_MS / 2,
    refetchInterval: SIGNED_MEDIA_REFRESH_MS,
    queryFn: async () => {
      const { data: pm } = await supabase
        .from("product_media")
        .select("media_assets(storage_key)")
        .eq("org_id", org!.id)
        .eq("product_id", id)
        .eq("status", "active")
        .order("is_primary", { ascending: false })
        .order("position")
        .limit(1)
        .maybeSingle();
      const asset = firstRelation((pm as { media_assets?: unknown } | null)?.media_assets);
      const key = (asset as { storage_key?: string } | null)?.storage_key;
      if (!key) return null;
      const urls = await signMediaUrls([key]);
      return urls[key] ?? null;
    },
  });

  const handleLifecycle = async (status: "draft" | "active" | "paused" | "archived") => {
    setLifecycleBusy(true);
    setLifecycleError("");
    try {
      await productApi.setStatus(org!.id, id!, status);
    } catch (e) {
      setLifecycleBusy(false);
      setLifecycleError((e as Error).message);
      return;
    }
    setLifecycleBusy(false);
    queryClient.invalidateQueries({ queryKey: ["product-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
  };

  const openSellingState = (action: "pause" | "resume") => {
    setSellingStateAction(action);
    setLifecycleError("");
    setLifecycleNotice("");
    setSellingStateOpen(true);
  };

  const handleSellingState = async () => {
    setSellingStateBusy(true);
    setLifecycleError("");
    setLifecycleNotice("");
    try {
      const result = await productApi.setSellingState(org!.id, id!, sellingStateAction);
      const failed = result.failures.map((channel) => channel.connector_name);
      const pending = result.pending.map((channel) => channel.connector_name);
      if (failed.length > 0) {
        setLifecycleError(
          `${sellingStateAction === "pause" ? "Pause" : "Resume"} completed with channel failures: ${failed.join(", ")}. Review the Channels tab and retry when the connection is healthy.`,
        );
      } else if (pending.length > 0) {
        if (result.pause_cycle_id) {
          setSellingStateTracking({ cycleId: result.pause_cycle_id, action: sellingStateAction });
        }
        setLifecycleNotice(
          `${sellingStateAction === "pause" ? "Pause" : "Resume"} requested. Marketplace updates are continuing in the background, and you can leave this page.`,
        );
      } else {
        const count = result.channels.length;
        setLifecycleNotice(
          sellingStateAction === "pause"
            ? `Selling paused${count ? ` on ${count} channel${count === 1 ? "" : "s"}` : ""}.`
            : `Selling resumed${count ? ` on ${count} channel${count === 1 ? "" : "s"}` : ""}.`,
        );
      }
      setSellingStateOpen(false);
    } catch (e) {
      setLifecycleError((e as Error).message);
    } finally {
      setSellingStateBusy(false);
      queryClient.invalidateQueries({ queryKey: ["product-detail", org?.id, id] });
      queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
      queryClient.invalidateQueries({ queryKey: ["catalogue-coverage", org?.id] });
      queryClient.invalidateQueries({ queryKey: ["channel-listings", org?.id, id] });
    }
  };

  const openDuplicate = () => {
    setDupError("");
    setDupName(data?.product ? `${data.product.name} (Copy)` : "");
    setDupOpen(true);
  };

  const handleDuplicate = async () => {
    setDupError("");
    if (!dupName.trim()) { setDupError("Enter a name for the copy."); return; }
    setDupBusy(true);
    try {
      const r = await productApi.duplicate(org!.id, id!, dupName.trim());
      setDupBusy(false);
      setDupOpen(false);
      queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
      if (r.productId) navigate(`/products/${r.productId}`);
    } catch (e) {
      setDupBusy(false);
      setDupError((e as Error).message);
    }
  };

  const openEdit = () => {
    if (!data?.product) return;
    setForm({
      name: data.product.name,
      reference: data.product.reference ?? "",
      barcode: (data.variants.length === 1 ? data.variants[0].barcode : data.product.barcode) ?? "",
      category_id: data.product.category_id ?? "",
      description: data.product.description ?? "",
      short_description: data.product.short_description ?? "",
      seo_title: data.product.seo_title ?? "",
      meta_description: data.product.meta_description ?? "",
      seo_keywords: data.product.seo_keywords ?? "",
      tags: (data.product.tags ?? []).join(", "),
      brand: data.product.brand ?? "",
      cost_price: data.product.cost_price != null ? String(data.product.cost_price) : "",
      selling_price: data.product.selling_price != null ? String(data.product.selling_price) : "",
      reorder_point: String(data.product.reorder_point ?? 0),
      reorder_qty: String(data.product.reorder_qty ?? 0),
      status: data.product.status,
      weight_kg: data.product.weight_kg != null ? String(data.product.weight_kg) : "",
      length_cm: data.product.length_cm != null ? String(data.product.length_cm) : "",
      width_cm: data.product.width_cm != null ? String(data.product.width_cm) : "",
      height_cm: data.product.height_cm != null ? String(data.product.height_cm) : "",
      manufacturer: data.product.manufacturer ?? "",
      model_number: data.product.model_number ?? "",
      mpn: data.product.mpn ?? "",
      country_of_origin: data.product.country_of_origin ?? "",
      hs_tariff_code: data.product.hs_tariff_code ?? "",
      customs_description: data.product.customs_description ?? "",
      unit_of_measure: effectiveUnitOfMeasure(data.product.unit, data.product.unit_of_measure),
      tax_profile: data.product.tax_profile ?? "",
      shipping_profile: data.product.shipping_profile ?? "",
      condition: data.product.condition ?? "",
    });
    setError("");
    setCatQuickAdd(false);
    setNewCategoryName("");
    setCatQuickError("");
    setEditOpen(true);
  };

  const handleQuickAddCategory = async () => {
    setCatQuickError("");
    const name = newCategoryName.trim();
    if (!name) {
      setCatQuickError("Enter a category name.");
      return;
    }
    const dup = findDuplicateCategory(data?.categoriesList ?? [], name, null);
    if (dup) {
      setCatQuickError(
        dup.archived
          ? `A category named "${dup.name}" already exists but is archived. Restore it from the Categories list instead.`
          : `A category named "${dup.name}" already exists — select it from the list instead.`,
      );
      return;
    }
    setCatQuickSaving(true);
    const { data: catData, error: catErr } = await supabase
      .from("categories")
      .insert({ org_id: org!.id, name })
      .select("id")
      .maybeSingle();
    setCatQuickSaving(false);
    if (catErr) {
      setCatQuickError(catErr.message);
      return;
    }
    if (catData?.id) {
      setForm((current) => ({ ...current, category_id: catData.id as string }));
      setNewCategoryName("");
      setCatQuickAdd(false);
      queryClient.invalidateQueries({ queryKey: ["product-detail", org?.id, id] });
    }
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await productApi.updateProduct(
        org!.id,
        id!,
        form,
        data?.variants.length === 1 ? data.variants[0].id : null,
      );
    } catch (err) {
      setSaving(false);
      setError((err as Error).message);
      return;
    }
    setSaving(false);
    setEditOpen(false);
    queryClient.invalidateQueries({ queryKey: ["product-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
  };

  const handleGenerateDesc = async () => {
    // Product-detail generation uses the same product.enrich_content task as Create Product.
    if (!data?.product || !org?.id) return;
    setAiDescLoading(true);
    setEnrichDraft(null);
    const product = data.product;
    const options = optionFactsFromVariants(data.variants);
    const currentCopy = {
      description: editOpen ? form.description : product.description,
      shortDescription: editOpen ? form.short_description : product.short_description,
      seoTitle: editOpen ? form.seo_title : product.seo_title,
      metaDescription: editOpen ? form.meta_description : product.meta_description,
      tags: editOpen ? form.tags : product.tags,
      seoKeywords: editOpen ? form.seo_keywords : product.seo_keywords,
    };
    const draft = await enrichProduct(
      {
        name: product.name,
        brand: product.brand,
        categoryName: product.categories?.name ?? null,
        productType: product.product_type,
        optionNames: options.optionNames,
        optionValues: options.optionValues,
        optionGroups: optionGroupsFromVariants(data.variants),
      },
      org.id,
      rewriteExisting ? { rewrite: true, existingCopy: currentCopy } : undefined,
    );
    setEnrichDraft(draft);
    setAiDescLoading(false);
  };

  const applyAiDesc = (overwriteConfirmed = false) => {
    if (!enrichDraft || !data?.product) return;
    const product = data.product;
    const base = editOpen
      ? form
      : {
          name: product.name,
          reference: product.reference ?? "",
          barcode: (data.variants.length === 1 ? data.variants[0].barcode : product.barcode) ?? "",
          category_id: product.category_id ?? "",
          description: product.description ?? "",
          short_description: product.short_description ?? "",
          seo_title: product.seo_title ?? "",
          meta_description: product.meta_description ?? "",
          seo_keywords: product.seo_keywords ?? "",
          tags: (product.tags ?? []).join(", "),
          brand: product.brand ?? "",
          cost_price: product.cost_price != null ? String(product.cost_price) : "",
          selling_price: product.selling_price != null ? String(product.selling_price) : "",
          reorder_point: String(product.reorder_point ?? 0),
          reorder_qty: String(product.reorder_qty ?? 0),
          status: product.status,
          weight_kg: product.weight_kg != null ? String(product.weight_kg) : "",
          length_cm: product.length_cm != null ? String(product.length_cm) : "",
          width_cm: product.width_cm != null ? String(product.width_cm) : "",
          height_cm: product.height_cm != null ? String(product.height_cm) : "",
          manufacturer: product.manufacturer ?? "",
          model_number: product.model_number ?? "",
          mpn: product.mpn ?? "",
          country_of_origin: product.country_of_origin ?? "",
          hs_tariff_code: product.hs_tariff_code ?? "",
          customs_description: product.customs_description ?? "",
          unit_of_measure: effectiveUnitOfMeasure(product.unit, product.unit_of_measure),
          tax_profile: product.tax_profile ?? "",
          shipping_profile: product.shipping_profile ?? "",
          condition: product.condition ?? "",
        };
    const current = {
      description: base.description,
      shortDescription: base.short_description,
      seoTitle: base.seo_title,
      metaDescription: base.meta_description,
      seoKeywords: base.seo_keywords || base.tags,
    };
    const draft = enrichableFieldsFromDraft(enrichDraft);
    const overwrite = overwriteConfirmed;
    if (!overwrite && enrichmentNeedsOverwriteConfirm(current, draft)) {
      const ok = window.confirm("Some fields already have your text. Replace them with the AI draft?");
      if (!ok) {
        const merged = mergeEnrichmentFields(current, draft, false);
        setForm({
          ...base,
          description: merged.description,
          short_description: merged.shortDescription,
          seo_title: merged.seoTitle,
          meta_description: merged.metaDescription,
          seo_keywords: merged.seoKeywords,
          tags: base.tags.trim() ? base.tags : enrichDraft.tags.join(", "),
        });
        setAiDescOpen(false);
        setEnrichDraft(null);
        setEditOpen(true);
        return;
      }
    }
    const merged = mergeEnrichmentFields(current, draft, true);
    setForm({
      ...base,
      description: merged.description,
      short_description: merged.shortDescription,
      seo_title: merged.seoTitle,
      meta_description: merged.metaDescription,
      seo_keywords: merged.seoKeywords,
      tags: enrichDraft.tags.join(", ") || base.tags,
    });
    setAiDescOpen(false);
    setEnrichDraft(null);
    setEditOpen(true);
  };

  const handlePublish = async (e: FormEvent) => {
    e.preventDefault();
    setPublishError("");
    const qty = Number(publishForm.channel_qty);
    if (!publishForm.connector_id || !publishForm.channel_qty || qty <= 0) {
      setPublishError("Choose a channel and set a positive quantity.");
      return;
    }
    setPublishSaving(true);
    try {
      await productApi.setChannelAllocation(
        org!.id,
        id!,
        publishForm.connector_id,
        qty,
        publishForm.safety_stock ? Number(publishForm.safety_stock) : 0,
      );
    } catch (err) {
      setPublishSaving(false);
      setPublishError((err as Error).message);
      return;
    }
    setPublishSaving(false);
    setPublishOpen(false);
    setPublishForm({ connector_id: "", channel_qty: "", safety_stock: "" });
    queryClient.invalidateQueries({ queryKey: ["channel-allocations", org?.id] });
  };

  const openArchiveConfirm = async () => {
    setArchiveError("");
    setArchiveReadiness(null);
    setArchiveOpen(true);
    setArchiveLoading(true);
    try {
      setArchiveReadiness(await productApi.checkArchiveReadiness(org!.id, id!));
    } catch (e) {
      setArchiveError((e as Error).message);
    }
    setArchiveLoading(false);
  };

  const openDeleteConfirm = async () => {
    setDeleteError("");
    setDeleteSafety(null);
    setDeleteWriteOffStock(false);
    setDeleteOpen(true);
    setDeleteLoading(true);
    try {
      setDeleteSafety(await productApi.checkDeleteSafety(org!.id, id!));
    } catch (e) {
      setDeleteError((e as Error).message);
    }
    setDeleteLoading(false);
  };

  const handleDelete = async () => {
    setDeleteBusy(true);
    setDeleteError("");
    try {
      const retired = await retireCatalogueProduct({ orgId: org!.id, productId: id! });
      if (!retired.success) {
        const retiredCode = typeof retired.data?.code === "string" ? retired.data.code : "";
        throw new Error(
          retiredCode === "PRODUCT_NOT_ARCHIVED" || /PRODUCT_NOT_ARCHIVED|Archive this product before deleting/i.test(retired.error ?? "")
            ? "Archive this product before deleting it permanently."
            : (retired.error ?? "Provider product retirement did not complete"),
        );
      }
      const result = await productApi.deletePermanently(org!.id, id!, deleteWriteOffStock, deleteSafety?.stock_confirmation ?? null);
      if (result.cleanup_media_asset_ids.length > 0) {
        await cleanupCatalogueMediaAssets(org!.id, result.cleanup_media_asset_ids).catch(() => undefined);
      }
    } catch (e) {
      setDeleteBusy(false);
      setDeleteWriteOffStock(false);
      const raw = (e as Error).message || "";
      setDeleteError(
        /PRODUCT_NOT_ARCHIVED|Archive this product before deleting/i.test(raw)
          ? "Archive this product before deleting it permanently."
          : raw,
      );
      try { setDeleteSafety(await productApi.checkDeleteSafety(org!.id, id!)); }
      catch { setDeleteSafety(null); }
      return;
    }
    setDeleteBusy(false);
    setDeleteOpen(false);
    queryClient.invalidateQueries({ queryKey: ["products", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["catalogue-coverage", org?.id] });
    navigate("/products");
  };

  if (isLoading) return <ProductDetailLoading />;

  if (isError || !data?.product) {
    return (
      <div>
        <PageHeader title="Product" backTo="/products" />
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load this product.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      </div>
    );
  }

  const p = data.product;
  const sellingActionForButton: "pause" | "resume" = p.status === "paused" ? "resume" : "pause";
  const flatCategories = flattenWithDepth((data.categoriesList ?? []).filter((c) => (c.status ?? "active") !== "archived"));
  const totalAvailable = data.balances.reduce((s, b) => s + Number(b.available ?? 0), 0);
  const totalOnHand = data.balances.reduce((s, b) => s + Number(b.on_hand ?? 0), 0);
  const bundleVariantId = p.product_type === "bundle" ? data.variants[0]?.id : null;

  const stockByVariant: Record<string, { available: number; onHand: number; allocated: number }> = {};
  data.balances.forEach((b) => {
    if (b.variant_id) {
      const cur = stockByVariant[b.variant_id] ?? { available: 0, onHand: 0, allocated: 0 };
      cur.available += Number(b.available ?? 0);
      cur.onHand += Number(b.on_hand ?? 0);
      cur.allocated += Number(b.allocated ?? 0);
      stockByVariant[b.variant_id] = cur;
    }
  });
  const singleSku = data.variants.length === 1 ? data.variants[0].sku : null;
  const isSimple = isSimpleProduct(data.variants.length);
  const moreKeys: ActiveTab[] = ["bundle", "media", "suppliers", "advanced", "history"];
  const isMoreActive = moreKeys.includes(activeTab);
  const tabBtnCls = (key: ActiveTab) =>
    `px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap flex items-center gap-2 transition-colors ${activeTab === key ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`;
  const moreBtnCls = (key: ActiveTab) =>
    `w-full px-3 py-2 text-sm font-medium rounded-md text-left whitespace-nowrap flex items-center gap-2 transition-colors ${activeTab === key ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`;

  const variantSellPrices = data.variants.map((v) => v.selling_price);
  const sellSummary = sellingPriceSummary(p.selling_price, variantSellPrices);
  const costSummary = costPriceSummary(p.cost_price, data.variants.map((v) => v.cost_price));
  const priceRange =
    sellSummary.min == null
      ? money(null)
      : sellSummary.min === sellSummary.max
        ? money(sellSummary.min)
        : `${money(sellSummary.min)} – ${money(sellSummary.max)}`;
  const costRange =
    costSummary.min == null
      ? money(null)
      : costSummary.min === costSummary.max
        ? money(costSummary.min)
        : `${money(costSummary.min)} – ${money(costSummary.max)}`;

  return (
    <div>
      <PageHeader title={p.name} backTo="/products">
        <button
          onClick={() => { setEnrichDraft(null); setRewriteExisting(false); setAiDescOpen(true); }}
          className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-sparkling-line" /> AI Description
        </button>
        <button
          onClick={() => { setPublishError(""); setPublishOpen(true); }}
          disabled={!channelConnectors || channelConnectors.length === 0}
          className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2 disabled:opacity-50"
          title={(!channelConnectors || channelConnectors.length === 0) ? "Add a connector first in Integrations" : "Publish to a sales channel"}
        >
          <i className="ri-store-2-line" /> Publish to Channel
        </button>
        {canManage && (p.status === "active" || p.status === "paused") && (
          <button onClick={() => openSellingState(sellingActionForButton)} disabled={sellingStateBusy || lifecycleBusy} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
            <i className={sellingActionForButton === "resume" ? "ri-play-circle-line" : "ri-pause-circle-line"} />
            {sellingActionForButton === "resume" ? "Activate" : "Pause"}
          </button>
        )}
        {canManage && p.status !== "archived" && (
          <button onClick={openArchiveConfirm} disabled={lifecycleBusy} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
            <i className="ri-archive-line" /> Archive
          </button>
        )}
        {canManage && p.status === "archived" && (
          <button onClick={() => handleLifecycle("active")} disabled={lifecycleBusy} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
            <i className="ri-play-circle-line" /> Activate
          </button>
        )}
        {canManage && p.status === "archived" && (
          <button onClick={openDeleteConfirm} className="px-4 py-2 bg-white border border-red-200 hover:bg-red-50 text-red-600 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
            <i className="ri-delete-bin-line" /> Delete permanently
          </button>
        )}
        {canManage && (
          <button onClick={openDuplicate} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
            <i className="ri-file-copy-line" /> Duplicate
          </button>
        )}
        <button onClick={openEdit} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-pencil-line" />
          Edit
        </button>
      </PageHeader>

      {lifecycleError && (
        <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{lifecycleError}</div>
      )}
      {lifecycleNotice && (
        <div className="mb-4 px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">{lifecycleNotice}</div>
      )}

      {org?.id && <PostCreateAttentionBanner orgId={org.id} productId={p.id} />}

      <div className="mb-6">
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 w-fit flex-wrap">
          <button onClick={() => setActiveTab("overview")} className={tabBtnCls("overview")} aria-current={activeTab === "overview" ? "page" : undefined}>
            <i className="ri-file-list-line" /> Overview
          </button>
          <button onClick={() => setActiveTab("variants")} className={tabBtnCls("variants")} aria-current={activeTab === "variants" ? "page" : undefined}>
            <i className="ri-grid-line" /> {variantSectionLabel(data.variants.length)}
            {!isSimple ? (
              <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{data.variants.length}</span>
            ) : null}
          </button>
          <button onClick={() => setActiveTab("inventory")} className={tabBtnCls("inventory")} aria-current={activeTab === "inventory" ? "page" : undefined}>
            <i className="ri-stack-line" /> Stock
          </button>
          <button onClick={() => setActiveTab("channels")} className={tabBtnCls("channels")} aria-current={activeTab === "channels" ? "page" : undefined}>
            <i className="ri-store-2-line" /> Selling
          </button>
          <button onClick={() => setActiveTab("storefront")} className={tabBtnCls("storefront")} aria-current={activeTab === "storefront" ? "page" : undefined} data-testid="tab-seo-storefront">
            SEO &amp; Storefront
          </button>
          <div className="relative">
            <button
              onClick={() => setMoreOpen((o) => !o)}
              className={`px-4 py-2 text-sm font-medium rounded-md whitespace-nowrap flex items-center gap-2 transition-colors ${isMoreActive ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
            >
              <i className="ri-more-2-line" /> More
              <i className={`ri-arrow-down-s-line transition-transform ${moreOpen ? "rotate-180" : ""}`} />
            </button>
            {moreOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMoreOpen(false)} />
                <div className="absolute left-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-lg p-1 z-20">
                  {p.product_type === "bundle" && (
                    <button onClick={() => { setActiveTab("bundle"); setMoreOpen(false); }} className={moreBtnCls("bundle")}>
                      <i className="ri-box-3-line" /> Bundle
                    </button>
                  )}
                  <button onClick={() => { setActiveTab("media"); setMoreOpen(false); }} className={moreBtnCls("media")}>
                    <i className="ri-image-line" /> Media
                  </button>
                  <button onClick={() => { setActiveTab("suppliers"); setMoreOpen(false); }} className={moreBtnCls("suppliers")}>
                    <i className="ri-building-2-line" /> Suppliers
                  </button>
                  <button onClick={() => { setActiveTab("advanced"); setMoreOpen(false); }} className={moreBtnCls("advanced")}>
                    <i className="ri-price-tag-3-line" /> Product Data
                  </button>
                  <button onClick={() => { setActiveTab("history"); setMoreOpen(false); }} className={moreBtnCls("history")}>
                    <i className="ri-time-line" /> History
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {activeTab === "overview" && (
        <OverviewTab
          product={p}
          quality={quality}
          optionCount={data.optionCount}
          listingCount={data.listingCount}
          variantCount={data.variants.length}
          totalAvailable={totalAvailable}
          totalOnHand={totalOnHand}
          priceRange={priceRange}
          costRange={costRange}
          suppliers={supData?.links ?? []}
          categoriesList={data.categoriesList}
          primaryImageUrl={primaryImage ?? null}
          singleSku={singleSku}
          onEdit={openEdit}
          onEditPrices={() => setPriceMatrixOpen(true)}
          onGoToTab={setActiveTab}
          orgId={org!.id}
          stockRowCount={data.balances.length}
        />
      )}

      {activeTab === "bundle" && bundleVariantId && (
        <BundleTab
          productId={id!}
          orgId={org!.id}
          parentVariantId={bundleVariantId}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ["product-detail", org?.id, id] })}
        />
      )}

      {activeTab === "variants" && (
        <VariantsTab productId={id!} orgId={org!.id} product={p} variants={data.variants} stockByVariant={stockByVariant} onEditPrices={() => setPriceMatrixOpen(true)} />
      )}

      {activeTab === "media" && (
        <MediaTab productId={id!} orgId={org!.id} variants={data?.variants ?? []} />
      )}

      {activeTab === "advanced" && (
        <AdvancedTab
          productId={id!}
          orgId={org!.id}
          product={p}
          variants={data.variants}
          externalIds={extData?.ids ?? []}
          connectors={extData?.connectors ?? []}
          onEdit={openEdit}
        />
      )}

      {activeTab === "inventory" && (
        <InventoryTab balances={data.balances} ledger={data.ledger} references={data.references} />
      )}

      {activeTab === "suppliers" && (
        <SuppliersTab
          productId={id!}
          orgId={org!.id}
          variants={data.variants}
          links={supData?.links ?? []}
          suppliers={supData?.suppliers ?? []}
        />
      )}

      {activeTab === "channels" && (
        <ChannelsTab
          productId={id!}
          orgId={org!.id}
          productName={p.name}
          productDescription={p.description}
          productCategoryName={p.categories?.name}
          productSellingPrice={p.selling_price}
          variants={data?.variants ?? []}
        />
      )}

      {activeTab === "storefront" && (
        <StorefrontTab
          product={p}
          orgId={org!.id}
          categoryName={p.categories?.name}
          primaryImageUrl={primaryImage ?? null}
          optionNames={optionFactsFromVariants(data.variants).optionNames}
          optionValues={optionFactsFromVariants(data.variants).optionValues}
          optionGroups={optionGroupsFromVariants(data.variants)}
        />
      )}

      {activeTab === "history" && (
        <HistoryTab historyData={historyData} activityData={activityData} />
      )}

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Edit Product"
        footer={
          <>
            <button onClick={() => setEditOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </>
        }
      >
        <form onSubmit={handleSave} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          {data?.variants.length === 1 && (
            <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500 flex items-start gap-2">
              <i className="ri-information-line mt-0.5 text-slate-400" />
              <span>This is a simple product. Its SKU and stock are managed in the <strong>SKU</strong> and <strong>Stock</strong> tabs.</span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="product-name">Name *</label>
              <input id="product-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="internal-reference">Internal reference</label>
              <input id="internal-reference" value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="product-edit-brand">Brand</label>
              <input id="product-edit-brand" data-testid="product-edit-brand" value={form.brand} onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Barcode</label>
              <input value={form.barcode} onChange={(e) => setForm((f) => ({ ...f, barcode: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select
                value={form.category_id}
                onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value }))}
                className={inputCls}
                data-testid="product-edit-category"
              >
                <option value="">No category</option>
                {flatCategories.map((c) => (
                  <option key={c.id} value={c.id}>{"\u00A0".repeat(c.depth * 2)}{c.name}</option>
                ))}
              </select>
              {!catQuickAdd ? (
                <button
                  type="button"
                  data-testid="product-edit-new-category"
                  onClick={() => { setCatQuickError(""); setCatQuickAdd(true); }}
                  className="mt-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap"
                >
                  <i className="ri-add-line" /> New category
                </button>
              ) : (
                <div className="mt-1 flex gap-1.5">
                  <input
                    autoFocus
                    data-testid="product-edit-new-category-name"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void handleQuickAddCategory();
                      }
                    }}
                    className={inputCls}
                    placeholder="e.g. T-Shirts"
                  />
                  <button
                    type="button"
                    data-testid="product-edit-add-category"
                    onClick={() => void handleQuickAddCategory()}
                    disabled={catQuickSaving}
                    className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap"
                  >
                    {catQuickSaving ? "Adding..." : "Add"}
                  </button>
                </div>
              )}
              {catQuickError && <p className="text-xs text-red-500 mt-1">{catQuickError}</p>}
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                disabled={p.status === "paused"}
                className={inputCls}
              >
                <option value="active">Active</option>
                <option value="paused" disabled>Paused — use the Pause selling action</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
              {p.status === "paused" && (
                <p className="text-[11px] text-slate-400 mt-1">Use Activate at the top of the product to resume the channels that were selling before the pause.</p>
              )}
            </div>
            <div>
              <label className={labelCls}>Cost price</label>
              <input type="number" step="0.01" value={form.cost_price} onChange={(e) => setForm((f) => ({ ...f, cost_price: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Normal selling price (product base)</label>
              <input type="number" step="0.01" value={form.selling_price} onChange={(e) => setForm((f) => ({ ...f, selling_price: e.target.value }))} className={inputCls} />
              <p className="text-[11px] text-slate-400 mt-1">Product default only. Inherited SKUs pick this up. A variant override never changes this field.</p>
            </div>
            <div>
              <label className={labelCls}>Reorder point</label>
              <input type="number" value={form.reorder_point} onChange={(e) => setForm((f) => ({ ...f, reorder_point: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Reorder quantity</label>
              <input type="number" value={form.reorder_qty} onChange={(e) => setForm((f) => ({ ...f, reorder_qty: e.target.value }))} className={inputCls} />
            </div>
            <div className="sm:col-span-2 pt-2">
              <p className="text-xs font-semibold text-slate-600 mb-2">Package / shipping defaults (used to pre-fill parcels)</p>
              <p className="text-[11px] text-slate-400 mb-2">These are package measurements, not a separate item-size record. Existing values were not reinterpreted.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <label className={labelCls} htmlFor="product-edit-weight">Package weight (kg)</label>
                  <input id="product-edit-weight" data-testid="product-edit-weight" type="number" step="0.01" min="0" value={form.weight_kg} onChange={(e) => setForm((f) => ({ ...f, weight_kg: e.target.value }))} className={inputCls} placeholder="0.5" />
                </div>
                <div>
                  <label className={labelCls}>Length (cm)</label>
                  <input type="number" step="0.1" min="0" value={form.length_cm} onChange={(e) => setForm((f) => ({ ...f, length_cm: e.target.value }))} className={inputCls} placeholder="20" />
                </div>
                <div>
                  <label className={labelCls}>Width (cm)</label>
                  <input type="number" step="0.1" min="0" value={form.width_cm} onChange={(e) => setForm((f) => ({ ...f, width_cm: e.target.value }))} className={inputCls} placeholder="15" />
                </div>
                <div>
                  <label className={labelCls}>Height (cm)</label>
                  <input type="number" step="0.1" min="0" value={form.height_cm} onChange={(e) => setForm((f) => ({ ...f, height_cm: e.target.value }))} className={inputCls} placeholder="10" />
                </div>
              </div>
            </div>
            <div className="sm:col-span-2 pt-2">
              <p className="text-xs font-semibold text-slate-600 mb-2">Product data</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div><label className={labelCls}>Manufacturer</label><input value={form.manufacturer} onChange={(e) => setForm((f) => ({ ...f, manufacturer: e.target.value }))} className={inputCls} placeholder="Acme" /></div>
                <div><label className={labelCls}>Model number</label><input value={form.model_number} onChange={(e) => setForm((f) => ({ ...f, model_number: e.target.value }))} className={inputCls} /></div>
                <div>
                  <label className={labelCls} htmlFor="product-edit-mpn">MPN</label>
                  <input id="product-edit-mpn" data-testid="product-edit-mpn" value={form.mpn} onChange={(e) => setForm((f) => ({ ...f, mpn: e.target.value }))} className={inputCls} placeholder="Leave blank if unknown" />
                  {form.mpn.trim() && !sanitizeManufacturerIdentifier(form.mpn) && (
                    <p className="text-[11px] text-amber-700 mt-1">Placeholder values such as N/A are stored as blank, not as a real MPN.</p>
                  )}
                </div>
                <div><label className={labelCls}>Country of origin</label><input value={form.country_of_origin} onChange={(e) => setForm((f) => ({ ...f, country_of_origin: e.target.value }))} className={inputCls} placeholder="CN" /></div>
                <div><label className={labelCls}>HS tariff code</label><input value={form.hs_tariff_code} onChange={(e) => setForm((f) => ({ ...f, hs_tariff_code: e.target.value }))} className={inputCls} placeholder="6109.10" /></div>
                <div><label className={labelCls} htmlFor="unit-of-measure">Unit of measure</label><input id="unit-of-measure" value={form.unit_of_measure} onChange={(e) => setForm((f) => ({ ...f, unit_of_measure: e.target.value }))} className={inputCls} placeholder="each" /></div>
                <div><label className={labelCls}>Tax profile</label><input value={form.tax_profile} onChange={(e) => setForm((f) => ({ ...f, tax_profile: e.target.value }))} className={inputCls} placeholder="Standard" /></div>
                <div><label className={labelCls}>Shipping profile</label><input value={form.shipping_profile} onChange={(e) => setForm((f) => ({ ...f, shipping_profile: e.target.value }))} className={inputCls} /></div>
                <div>
                  <label className={labelCls} htmlFor="product-edit-condition">Condition</label>
                  <select
                    id="product-edit-condition"
                    data-testid="product-edit-condition"
                    value={conditionSelectValue(form.condition)}
                    onChange={(e) => setForm((f) => ({ ...f, condition: e.target.value }))}
                    className={inputCls}
                  >
                    <option value="">Not set</option>
                    {CANONICAL_CONDITION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {form.condition && !CANONICAL_CONDITION_OPTIONS.some((o) => o.value === conditionSelectValue(form.condition)) && (
                      <option value={form.condition}>{form.condition}</option>
                    )}
                  </select>
                </div>
                <div className="sm:col-span-3"><label className={labelCls}>Customs description</label><input value={form.customs_description} onChange={(e) => setForm((f) => ({ ...f, customs_description: e.target.value }))} className={inputCls} /></div>
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Description</label>
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={inputCls} rows={3} maxLength={500} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Short description</label>
              <textarea value={form.short_description} onChange={(e) => setForm((f) => ({ ...f, short_description: e.target.value }))} className={inputCls} rows={2} maxLength={200} />
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs text-slate-500">SEO title, meta description, keywords, URL slug, and Open Graph settings are edited on the <strong>SEO &amp; Storefront</strong> tab.</p>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Tags (comma separated)</label>
              <input value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} className={inputCls} placeholder="summer, unisex, cotton" />
            </div>
          </div>
        </form>
      </Modal>

      {/* AI Description Generator */}
      <Modal
        open={aiDescOpen}
        onClose={() => { setAiDescOpen(false); setEnrichDraft(null); }}
        title="AI Product Description & SEO"
        footer={
          <>
            <button onClick={() => { setAiDescOpen(false); setEnrichDraft(null); }} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            {enrichDraft ? (
              <button onClick={() => applyAiDesc(false)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
                <i className="ri-check-line" /> Apply draft to form
              </button>
            ) : (
              <button onClick={handleGenerateDesc} disabled={aiDescLoading} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
                <i className={`${aiDescLoading ? "ri-loader-4-line animate-spin" : "ri-sparkling-line"}`} />
                {aiDescLoading ? "Generating..." : "Generate draft"}
              </button>
            )}
          </>
        }
      >
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <p className="text-xs font-semibold text-slate-600">{p.name}</p>
            <p className="text-xs text-slate-500 mt-0.5">{p.categories?.name ?? "No category"} · draft only — nothing is saved until you apply and then save the product.</p>
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-1"
              checked={rewriteExisting}
              onChange={(e) => setRewriteExisting(e.target.checked)}
              data-testid="ai-rewrite-existing"
            />
            <span>Rewrite existing operator copy (sends current description/SEO only for this request)</span>
          </label>
          {enrichDraft && (
            <div className="space-y-3 text-sm text-slate-700">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {enrichmentDraftCaption(enrichDraft)}
              </p>
              <div>
                <p className="text-xs font-medium text-slate-500">Description</p>
                <p className="whitespace-pre-wrap text-sm">{enrichDraft.description}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Short description</p>
                <p>{enrichDraft.shortDescription || "—"}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">SEO title</p>
                <p>{enrichDraft.seoTitle || "—"}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Meta description</p>
                <p>{enrichDraft.metaDescription || "—"}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500">Keywords / tags</p>
                <p>{enrichDraft.seoKeywords || enrichDraft.tags.join(", ") || "—"}</p>
              </div>
              <p className="text-xs text-slate-400">Applying fills the edit form only. SKU, GTIN, brand, price, and stock are not changed by this draft. Save the product to persist.</p>
            </div>
          )}
          {!enrichDraft && (
            <div className="border border-dashed border-slate-200 rounded-lg p-4 text-center">
              <i className="ri-sparkling-2-line text-2xl text-slate-300 mb-2 block" />
              <p className="text-sm text-slate-500">Generate a marketing draft from the product name, brand, category, and options.</p>
              <p className="text-xs text-slate-400 mt-1">Uses the same task as Create Product. Falls back to an internal template if AI is unavailable.</p>
            </div>
          )}
        </div>
      </Modal>

      {/* Publish to Channel */}
      <Modal
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        title="Publish to Sales Channel"
        footer={
          <>
            <button onClick={() => setPublishOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handlePublish} disabled={publishSaving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
              <i className="ri-store-2-line" /> {publishSaving ? "Publishing..." : "Publish"}
            </button>
          </>
        }
      >
        <form onSubmit={handlePublish} className="space-y-4">
          {publishError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{publishError}</div>}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
            <i className="ri-information-line mr-1" />
            This sets the channel allocation quantity for {p.name}. The actual listing on eBay/BigCommerce goes live once the connector credentials are configured in Integrations.
          </div>
          <div>
            <label className={labelCls}>Channel (connector) *</label>
            <select required value={publishForm.connector_id} onChange={(e) => setPublishForm((f) => ({ ...f, connector_id: e.target.value }))} className={inputCls}>
              <option value="">Select channel...</option>
              {(channelConnectors ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Qty to publish *</label>
              <input type="number" min="1" value={publishForm.channel_qty} onChange={(e) => setPublishForm((f) => ({ ...f, channel_qty: e.target.value }))} className={inputCls} placeholder="e.g. 10" />
            </div>
            <div>
              <label className={labelCls}>Safety stock</label>
              <input type="number" min="0" value={publishForm.safety_stock} onChange={(e) => setPublishForm((f) => ({ ...f, safety_stock: e.target.value }))} className={inputCls} placeholder="e.g. 2" />
            </div>
          </div>
          <p className="text-xs text-slate-400">Available stock: {totalAvailable}. Safety stock is held back and not published.</p>
        </form>
      </Modal>

      <Modal
        open={dupOpen}
        onClose={() => setDupOpen(false)}
        title="Duplicate Product"
        footer={
          <>
            <button onClick={() => setDupOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleDuplicate} disabled={dupBusy} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {dupBusy ? "Duplicating..." : "Create Copy"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {dupError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{dupError}</div>}
          <div>
            <label className={labelCls}>New product name *</label>
            <input autoFocus value={dupName} onChange={(e) => setDupName(e.target.value)} className={inputCls} />
          </div>
          <p className="text-xs text-slate-500">A copy is created as a <strong>Draft</strong> with regenerated SKUs. Inventory, ledger, order history, external marketplace IDs and identifiers are NOT copied.</p>
        </div>
      </Modal>

      <Modal
        open={sellingStateOpen}
        onClose={() => { if (!sellingStateBusy) setSellingStateOpen(false); }}
        title={sellingStateAction === "pause" ? "Pause selling this product?" : "Resume selling this product?"}
        footer={
          <>
            <button
              onClick={() => setSellingStateOpen(false)}
              disabled={sellingStateBusy}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={() => void handleSellingState()}
              disabled={sellingStateBusy}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {sellingStateBusy
                ? (sellingStateAction === "pause" ? "Pausing..." : "Resuming...")
                : (sellingStateAction === "pause" ? "Pause selling" : "Resume selling")}
            </button>
          </>
        }
      >
        {sellingStateAction === "pause" ? (
          <div className="space-y-3 text-sm text-slate-600">
            <p>This will temporarily stop this product from being sold on all currently published channels.</p>
            <p>Your product, variants, inventory, prices, marketplace mappings and history will remain unchanged. You can activate the product again when it is ready to sell.</p>
            <div className="px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-lg text-amber-800">
              Tradivo will publish zero sellable quantity to each live channel. If any channel cannot be updated, it will be reported instead of being treated as paused.
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-sm text-slate-600">
            <p>Tradivo will attempt to resume this product on the sales channels that were active before it was paused.</p>
            <p>Channels that were not selling when the pause was requested will stay unchanged. If a channel cannot be resumed, Tradivo will report the failure instead of silently publishing elsewhere.</p>
          </div>
        )}
      </Modal>

      <Modal
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        title="Archive Product"
        footer={
          <>
            <button onClick={() => setArchiveOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button
              onClick={() => { handleLifecycle("archived"); setArchiveOpen(false); }}
              disabled={lifecycleBusy || archiveLoading || !archiveReadiness?.can_archive}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {lifecycleBusy ? "Archiving..." : "Archive"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {archiveError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{archiveError}</div>}
          {archiveLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-2"><i className="ri-loader-4-line animate-spin" /> Checking operational references...</div>
          ) : archiveReadiness ? (
            <>
              {archiveReadiness.blockers.length > 0 ? (
                <div>
                  <p className="text-sm font-medium text-slate-800 mb-2">This product cannot be archived yet:</p>
                  <ul className="space-y-1.5">
                    {archiveReadiness.blockers.map((b) => (
                      <li key={b.code} className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                        <i className="ri-error-warning-line mt-0.5" /> {b.message}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-slate-400 mt-2">Resolve these references first. Historical records are never deleted.</p>
                </div>
              ) : (
                <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">
                  <i className="ri-check-line mr-1" /> No blocking references. Safe to archive.
                </div>
              )}
              {archiveReadiness.warnings.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-800 mb-2">Please note:</p>
                  <ul className="space-y-1.5">
                    {archiveReadiness.warnings.map((w) => (
                      <li key={w.code} className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                        <i className="ri-alert-line mt-0.5" /> {w.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : null}
        </div>
      </Modal>

      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete Product Permanently"
        footer={
          <>
            <button onClick={() => setDeleteOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button
              onClick={handleDelete}
              disabled={
                deleteBusy
                || deleteLoading
                || !deleteSafety?.can_delete
                || (deleteSafety.requires_stock_disposition && (!deleteWriteOffStock || !deleteSafety.stock_confirmation))
              }
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap"
            >
              {deleteBusy ? "Deleting..." : "Delete permanently"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {deleteError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{deleteError}</div>}
          <p className="text-sm text-slate-600">
            This removes the live catalogue product, variants and disposable media. Completed sales and historical inventory records remain available independently.
          </p>
          {deleteLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-2"><i className="ri-loader-4-line animate-spin" /> Checking live operational dependencies...</div>
          ) : deleteSafety ? (
            <>
              {deleteSafety.blockers.length > 0 ? (
                <div>
                  <p className="text-sm font-medium text-slate-800 mb-2">This product cannot be deleted yet:</p>
                  <ul className="space-y-1.5">
                    {deleteSafety.blockers.map((b) => (
                      <li key={b.code} className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                        <i className="ri-error-warning-line mt-0.5" /> {b.message}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-emerald-700">
                  <i className="ri-check-line mr-1" /> No unresolved operational dependencies.
                </div>
              )}

              {deleteSafety.warnings.length > 0 && (
                <ul className="space-y-1.5">
                  {deleteSafety.warnings.map((w) => (
                    <li key={w.code} className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                      <i className="ri-alert-line mt-0.5" /> {w.message}
                    </li>
                  ))}
                </ul>
              )}

              {deleteSafety.requires_stock_disposition && <StockDispositionPreview snapshot={deleteSafety.stock_confirmation} />}
              {deleteSafety.requires_stock_disposition && (
                <label className="flex items-start gap-2 text-sm text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <input
                    type="checkbox"
                    checked={deleteWriteOffStock}
                    onChange={(e) => setDeleteWriteOffStock(e.target.checked)}
                    className="mt-1"
                  />
                  <span>
                    Write off <strong>{deleteSafety.on_hand}</strong> unit(s) from active inventory through the inventory ledger, then delete this product.
                  </span>
                </label>
              )}

              <p className="text-xs text-slate-500">
                Historical sales preserved: {deleteSafety.historical_sales}. Historical stock movements preserved: {deleteSafety.historical_movements}.
              </p>
            </>
          ) : null}
        </div>
      </Modal>

      {org && (
        <QuickVariantPriceModal
          open={priceMatrixOpen}
          onClose={() => setPriceMatrixOpen(false)}
          orgId={org.id}
          productId={p.id}
          productName={p.name}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ["product-detail", org.id, id] });
            queryClient.invalidateQueries({ queryKey: ["products", org.id] });
            queryClient.invalidateQueries({ queryKey: ["catalogue-coverage", org.id] });
          }}
        />
      )}
    </div>
  );
}